import Decimal from 'decimal.js';

import { arrayFromN } from '@helpers/common/array.helpers';

import {
  ALL_ZEROS_REGEX,
  LEADING_ZEROS_REGEX,
} from '@constants/common/regex.constants';

export const MIN_MINOR_FRACTION = 3;

export type Numerable = number | string | Decimal | bigint | undefined | null;

export const toBN = (origVal: Numerable, dividerDecimals = 0): Decimal => {
  Decimal.set({
    precision: 1000,
    rounding: Decimal.ROUND_DOWN,
  });
  if (!origVal) return new Decimal(0);

  const val = typeof origVal === 'bigint' ? origVal.toString() : origVal;
  if (Number.isNaN(Number(val)) || val === null) {
    return new Decimal(val);
  }
  if (val instanceof Decimal) {
    return dividerDecimals !== undefined
      ? val.dividedBy(new Decimal(10).toPower(dividerDecimals))
      : val;
  }

  return dividerDecimals
    ? new Decimal(val.toString()).div(new Decimal(10).pow(dividerDecimals))
    : new Decimal(val.toString());
};

export const formatBNWithCommas = (
  val: Numerable,
  precision: Numerable = 2,
  dividerDecimals = 0,
  lessSign = false,
  toSignify = false,
) => {
  if (
    val === null ||
    val === undefined ||
    Number.isNaN(Number(val)) ||
    toBN(val).equals(0)
  ) {
    return '0';
  }

  const numPrecision = Number(precision);
  const numVal = Number(toBN(val, dividerDecimals));
  const targetMinValue = 1 / 10 ** numPrecision;

  if (lessSign && numVal < targetMinValue && Number(val) > 0) {
    return `<0.${arrayFromN(numPrecision - 1)
      .map(() => '0')
      .join('')}1`;
  }

  const signifyNumVal = toBN(numVal).toSignificantDigits().toNumber();
  const parts = String(signifyNumVal).split('.');
  const countDigitsAfterDot = parts[1] ? parts[1].length : 0;

  const formatOptions = toSignify
    ? {
        maximumFractionDigits:
          numPrecision > countDigitsAfterDot
            ? countDigitsAfterDot
            : numPrecision,
        minimumFractionDigits:
          numPrecision > countDigitsAfterDot
            ? countDigitsAfterDot
            : numPrecision,
      }
    : {
        maximumFractionDigits: numPrecision,
        minimumFractionDigits: numPrecision,
      };

  return signifyNumVal.toLocaleString('en-US', formatOptions);
};

export const applyDecimals = (val: Numerable, decimals: number): Decimal =>
  toBN(val).mul(new Decimal(10).toPower(decimals));

export const getPrecision = (val: Decimal): number =>
  (val.gt(0) && val.lt(1)) || (val.lt(0) && val.gt(-1)) ? 2 : 0;

export const nFormatter = (num: Decimal, precision?: number) => {
  if (num.equals(0)) {
    return '0';
  }

  const precisionFactor = precision || getPrecision(num);

  const lookup = [
    { value: toBN(1), symbol: '' },
    { value: toBN(1e3), symbol: 'K' },
    { value: toBN(1e6), symbol: 'M' },
    { value: toBN(1e9), symbol: 'B' },
    { value: toBN(1e12), symbol: 'T' },
    { value: toBN(1e15), symbol: 'P' },
    { value: toBN(1e18), symbol: 'E' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;

  if (num.lt(10000)) {
    return formatBNWithCommas(num, precisionFactor);
  }

  const item = lookup
    .slice()
    .reverse()
    .find((lookupItem) => num.gte(lookupItem.value));
  return item
    ? num.dividedBy(item.value).toFixed(precisionFactor).replace(rx, '$1') +
        item.symbol
    : '0';
};

export const removeDecimals = (val: Numerable, decimals: number): Decimal =>
  toBN(val, decimals);

export const processFractionWithPrecision = (
  input: Numerable,
  precision = 2,
  precisionAfterZeros = 5,
) => {
  input = toBN(input).toFixed().toString().trim();

  if (toBN(input).isZero()) {
    return {
      zerosCount: null,
      mantissa: null,
      value: '0',
    };
  }

  if (toBN(input).gte(1)) {
    return {
      zerosCount: null,
      mantissa: null,
      value: formatBNWithCommas(input, precision),
    };
  }

  const decimalPointIndex = input.indexOf('.');
  let fractionalPart = '';

  if (decimalPointIndex !== -1) {
    fractionalPart = input.slice(decimalPointIndex + 1);
  }

  if (ALL_ZEROS_REGEX.test(fractionalPart)) {
    return {
      zerosCount: null,
      mantissa: null,
      value: '0',
    };
  }

  const zerosCount = fractionalPart.match(LEADING_ZEROS_REGEX)?.[0].length || 0;

  let cleanedMantissa = fractionalPart.slice(zerosCount);
  cleanedMantissa = cleanedMantissa.slice(0, precision);

  if (toBN(zerosCount).eq(0)) {
    return {
      zerosCount: null,
      mantissa: null,
      value: toBN(input).toFixed(precision),
    };
  }

  if (toBN(zerosCount).lte(MIN_MINOR_FRACTION) && toBN(input).lt('0.01')) {
    return {
      zerosCount: null,
      mantissa: null,
      value: toBN(input).toFixed(precisionAfterZeros),
    };
  }

  return {
    zerosCount,
    mantissa: cleanedMantissa,
    value: `0.0 (${zerosCount}) ${cleanedMantissa}`,
  };
};
